import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { FOCUSABLE_SELECTOR } from './useTrapFocus';

const VISIBLE_CLASS = 'is-visible';

interface MobileNavFocus {
    /** Callback ref to attach to the `<nav class="usa-nav">` element. */
    setNavRef: (node: HTMLElement | null) => void;
    /** Stable ref object mirroring the nav node, for use with `useTrapFocus`. */
    navRef: RefObject<HTMLElement | null>;
    /** Whether the mobile nav is currently open (`is-visible`). */
    isOpen: boolean;
}

/**
 * Manages TalkBack/keyboard focus for the USWDS mobile navigation.
 *
 * USWDS auto-inits on DOMContentLoaded, before the React header exists, so its
 * built-in focus trap is never created and its `toggleNav` throws before it can
 * move focus into the menu. This hook restores that behavior in React: it watches
 * the `is-visible` class USWDS still toggles on the nav, moves focus to the first
 * menu option on open, and returns focus to the menu button on close. Pair the
 * returned `navRef`/`isOpen` with `useTrapFocus` to trap focus while open.
 */
export const useMobileNavFocus = (openerSelector = '.usa-menu-btn'): MobileNavFocus => {
    const navRef = useRef<HTMLElement | null>(null);
    const [navNode, setNavNode] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const setNavRef = useCallback((node: HTMLElement | null) => {
        navRef.current = node;
        setNavNode(node);
    }, []);

    useEffect(() => {
        if (!navNode) return;

        const syncOpenState = () => setIsOpen(navNode.classList.contains(VISIBLE_CLASS));

        const observer = new MutationObserver(syncOpenState);
        observer.observe(navNode, { attributes: true, attributeFilter: ['class'] });
        syncOpenState();

        return () => observer.disconnect();
    }, [navNode]);

    useEffect(() => {
        if (!navNode) return;

        if (isOpen) {
            // Focus the first menu option (e.g. "Espanol"), skipping the Close button.
            const primary = navNode.querySelector<HTMLElement>('.usa-nav__primary') ?? navNode;
            const firstOption = primary.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            if (firstOption) {
                const animationFrame = requestAnimationFrame(() => firstOption.focus());
                return () => cancelAnimationFrame(animationFrame);
            }
        } else if (navNode.contains(document.activeElement)) {
            // Menu closed while focus was still inside it: return focus to the trigger.
            const opener = document.querySelector<HTMLElement>(openerSelector);
            opener?.focus();
        }
    }, [isOpen, navNode, openerSelector]);

    return { setNavRef, navRef, isOpen };
};
